/**
 * Map3DViewFinal - Working Google Maps 3D Implementation
 * RoofMapper v3 - NIKO Energy
 * 
 * Features:
 * - Starts in bird's eye view (90° / tilt=0)
 * - Click to place pin
 * - Pin adapts to roof height (RELATIVE_TO_GROUND)
 * - Exposes map instance for external control
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, MonitorOff, Check, Copy } from 'lucide-react';
import { useMap } from '../../contexts/MapContext';
import { useSolar } from '../../contexts/SolarContext';

// Extend Window type for Google Maps API key
declare global {
  interface Window {
    _GOOGLE_MAPS_API_KEY?: string;
  }
}

// Default center (Mexico City)
const DEFAULT_CENTER = {
  lat: 19.42241280654638,
  lng: -99.21513116670044,
  altitude: 60,
};

// TECH-58: Google Maps 3D requiere WebGL2 con aceleracion por hardware. Si el
// navegador esta en modo software (chrome://gpu muestra "Software only"), el
// renderer de Google falla y muestra un cartel generico "Uy! Se produjo un
// error". Detectamos eso antes de montar el componente y mostramos un mensaje
// claro al usuario.
function isWebGL2HardwareAccelerated(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2');
    if (!gl) return false;
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return true; // sin info, asumimos que esta OK
    const renderer = String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
    // Renderers de software conocidos
    return !/SwiftShader|Software|llvmpipe|ANGLE \(.*Microsoft Basic Render/i.test(renderer);
  } catch {
    return false;
  }
}

// Helper function to calculate distance between lat/lng points (Haversine formula)
function calculatePathDistance(points: Array<{ lat: number; lng: number }>): number {
  if (points.length < 2) return 0;
  
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    
    // Skip if either point is undefined (shouldn't happen, but handle it)
    if (!p1 || !p2) continue;
    
    const R = 6371e3; // Earth radius in meters
    const φ1 = (p1.lat * Math.PI) / 180;
    const φ2 = (p2.lat * Math.PI) / 180;
    const Δφ = ((p2.lat - p1.lat) * Math.PI) / 180;
    const Δλ = ((p2.lng - p1.lng) * Math.PI) / 180;
    
    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    
    total += R * c;
  }
  return total;
}

interface Map3DViewFinalProps {
  onMapReady?: (map: any) => void;
  drawingMode?: 'none' | 'measure' | 'polygon' | 'panel';
}

function Map3DUnavailableOverlay() {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      // ignore
    }
  };

  const CodeChip = ({ text, k }: { text: string; k: string }) => (
    <button
      onClick={() => copy(text, k)}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors font-mono text-[0.85em] text-gray-800 border border-gray-200"
      title="Copiar al portapapeles"
    >
      {text}
      {copied === k ? (
        <Check className="w-3 h-3 text-green-600" />
      ) : (
        <Copy className="w-3 h-3 text-gray-400" />
      )}
    </button>
  );

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-6 overflow-auto">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        {/* Header con accent Niko */}
        <div className="bg-[#EBFD03] px-6 py-4 flex items-center gap-3">
          <div className="relative">
            <Box className="w-7 h-7 text-black" strokeWidth={2.2} />
            <MonitorOff className="w-4 h-4 absolute -bottom-1 -right-1 text-black bg-[#EBFD03] rounded-full" strokeWidth={2.5} />
          </div>
          <div>
            <h3 className="text-base font-bold text-black leading-tight">
              Vista 3D no disponible
            </h3>
            <p className="text-xs text-black/70">
              Tu navegador necesita aceleración por hardware
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-700 leading-relaxed">
            Google Maps 3D requiere <span className="font-semibold">WebGL2 con aceleración por hardware</span>.
            Tu Chrome lo está corriendo en modo software, por eso falló al renderizar.
          </p>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Cómo activarlo
            </p>
            <ol className="space-y-2.5 text-sm text-gray-700">
              <li className="flex gap-3">
                <span className="flex-none w-5 h-5 rounded-full bg-gray-900 text-white text-xs flex items-center justify-center font-semibold mt-0.5">1</span>
                <span>
                  Abrí <CodeChip text="chrome://settings/system" k="settings" /> en una pestaña nueva
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex-none w-5 h-5 rounded-full bg-gray-900 text-white text-xs flex items-center justify-center font-semibold mt-0.5">2</span>
                <span>
                  Activá <span className="font-medium">"Usar aceleración por hardware cuando esté disponible"</span>
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex-none w-5 h-5 rounded-full bg-gray-900 text-white text-xs flex items-center justify-center font-semibold mt-0.5">3</span>
                <span>Reiniciá Chrome completamente y volvé a entrar</span>
              </li>
            </ol>
          </div>

          <div className="border-t border-gray-100 pt-3">
            <p className="text-xs text-gray-500 leading-relaxed">
              Para verificar que quedó bien, abrí <CodeChip text="chrome://gpu" k="gpu" /> y buscá <span className="font-medium text-gray-700">WebGL2</span> — debe decir <span className="font-medium text-green-700">"Hardware accelerated"</span>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Map3DViewFinal({ onMapReady, drawingMode = 'none' }: Map3DViewFinalProps): JSX.Element | null {
  const { setPinPosition, pinPosition, cameraState, setCameraState } = useMap();
  const { buildingInsights } = useSolar();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const polygonsRef = useRef<any[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [is3DUnavailable] = useState(() => !isWebGL2HardwareAccelerated());
  
  // Drawing state
  const drawingPointsRef = useRef<Array<{ lat: number; lng: number }>>([]);
  const drawingPolygonRef = useRef<any>(null);
  const drawingMarkersRef = useRef<any[]>([]); // Visual markers for polygon points
  const measurePointsRef = useRef<Array<{ lat: number; lng: number }>>([]);
  const measureLineRef = useRef<any>(null);
  const measureMarkersRef = useRef<any[]>([]); // Visual markers for measure points
  const solarPanelsRef = useRef<any[]>([]); // 3D solar panel models

  // Helper to add visual marker at a point
  const addVisualMarker = async (lat: number, lng: number, color: string = '#EBFD03'): Promise<any> => {
    try {
      const { Marker3DElement, AltitudeMode } = await window.google.maps.importLibrary('maps3d') as any;
      const marker = new Marker3DElement({
        position: { lat, lng, altitude: 2 },
        altitudeMode: AltitudeMode.RELATIVE_TO_GROUND,
      });
      
      mapRef.current.append(marker);
      return marker;
    } catch (error) {
      console.error('❌ Error creating visual marker:', error);
      return null;
    }
  };

  // Initialize map
  useEffect(() => {
    if (is3DUnavailable) {
      console.warn('⚠️ 3D Maps unavailable: WebGL2 hardware acceleration not detected');
      return;
    }
    const initMap = async () => {
      try {
        if (!window.google?.maps?.importLibrary) {
          console.error('Google Maps API not loaded');
          return;
        }

        if (!containerRef.current) {
          console.error('Container ref not ready');
          return;
        }

        const { Map3DElement } = await window.google.maps.importLibrary('maps3d') as any;

        const center = cameraState.center || DEFAULT_CENTER;
        
        // Create 3D map with bird's eye view (90° top-down) - full block view
        // KEY: altitude in center controls the height! Not range!
            const startCenter = {
              lat: center.lat,
              lng: center.lng,
              altitude: 500  // THIS controls how high up we are!
            };

            const map = new Map3DElement({
              center: startCenter,
              tilt: 0,           // 0 = bird's eye / 90° top-down view
              heading: 0,
              range: 2000,       // Range is field of view/zoom
              // Valid modes: 'HYBRID' (default satellite), 'UNOBSTRUCTED' (no trees/buildings clutter)
              // Using HYBRID but polygons will be very visible with extrusion
              mode: 'HYBRID',
              gestureHandling: 'COOPERATIVE'
            });

        // Append to container (respects parent bounds for split-screen)
        containerRef.current.appendChild(map);
        
        // Style to fill parent container
        map.style.position = 'absolute';
        map.style.top = '0';
        map.style.left = '0';
        map.style.width = '100%';
        map.style.height = '100%';
        map.style.zIndex = '0';

        mapRef.current = map;

        // Listen for clicks
        map.addEventListener('gmp-click', async (event: any) => {
          
          const position = event.detail?.position || event.position;
          
          if (!position) {
            return;
          }

          const { lat, lng } = position;

          // Handle different modes
          if (drawingMode === 'polygon') {
            // Add point to polygon drawing
            drawingPointsRef.current.push({ lat, lng });
            
            // Add visual marker
            try {
              const marker = await addVisualMarker(lat, lng, '#EBFD03');
              if (marker) {
                drawingMarkersRef.current.push(marker);
              } else {
                console.warn('⚠️ Marker creation returned null');
              }
            } catch (err) {
              console.error('❌ Failed to add marker:', err);
            }
            
            // Update or create polygon (only if 3+ points for a visible polygon)
            if (drawingPointsRef.current.length >= 3) {
              try {
                if (!drawingPolygonRef.current) {
                  // Create new polygon
                  const { Polygon3DElement, AltitudeMode } = await window.google.maps.importLibrary('maps3d') as any;
                  const polygon = new Polygon3DElement({
                    strokeColor: '#EBFD0380',
                    strokeWidth: 4,
                    fillColor: '#EBFD0340',
                    drawsOccludedSegments: true,
                    altitudeMode: AltitudeMode.CLAMP_TO_GROUND,
                  });
                  drawingPolygonRef.current = polygon;
                  mapRef.current.append(polygon);
                }
                // Update path
                drawingPolygonRef.current.path = [...drawingPointsRef.current];
              } catch (err) {
                console.error('❌ Failed to create/update polygon:', err);
              }
            }
          } else if (drawingMode === 'measure') {
            // Add point to measurement
            measurePointsRef.current.push({ lat, lng });
            
            // Add visual marker (red for measurement)
            const marker = await addVisualMarker(lat, lng, '#FF0000');
            if (marker) {
              measureMarkersRef.current.push(marker);
            }
            
            // Update or create polyline
            if (measurePointsRef.current.length >= 2) {
              if (!measureLineRef.current) {
                // Create new polyline
                const { Polyline3DElement, AltitudeMode } = await window.google.maps.importLibrary('maps3d') as any;
                const polyline = new Polyline3DElement({
                  strokeColor: '#FF0000',
                  strokeWidth: 5,
                  drawsOccludedSegments: true,
                  altitudeMode: AltitudeMode.CLAMP_TO_GROUND,
                });
                measureLineRef.current = polyline;
                mapRef.current.append(polyline);
              }
              // Update path
              measureLineRef.current.path = [...measurePointsRef.current];
              
              // Calculate distance
              const totalDistance = calculatePathDistance(measurePointsRef.current);
            }
          } else if (drawingMode === 'panel') {
            // Place a 3D solar panel using Model3DElement
            try {
              const { Model3DElement } = await window.google.maps.importLibrary('maps3d') as any;
              
              // Try to find the closest roof segment to get accurate height
              let roofHeight = 0;
              if (buildingInsights?.solarPotential?.roofSegmentStats) {
                const segments = buildingInsights.solarPotential.roofSegmentStats;
                
                // Find closest roof segment
                let minDistance = Infinity;
                let closestSegment = null;
                
                for (const segment of segments) {
                  const segLat = segment.center.latitude;
                  const segLng = segment.center.longitude;
                  const distance = Math.sqrt(
                    Math.pow(lat - segLat, 2) + Math.pow(lng - segLng, 2)
                  );
                  if (distance < minDistance) {
                    minDistance = distance;
                    closestSegment = segment;
                  }
                }
                
                if (closestSegment) {
                  roofHeight = closestSegment.planeHeightAtCenterMeters;
                }
              }
              
              // Use a simple box model as solar panel
              // For now, we'll use Google's windmill as a placeholder
              // TODO: Replace with actual solar panel .glb model
              const panel = new Model3DElement({
                src: 'https://storage.googleapis.com/maps-web-api.appspot.com/gltf/windmill.glb',
                position: { lat, lng, altitude: roofHeight },
                altitudeMode: 'ABSOLUTE', // Use absolute altitude since we have exact height
                orientation: { heading: 0, tilt: 270, roll: 0 }, // Lay flat
                scale: 0.1, // Small scale
              });
              
              mapRef.current.append(panel);
              solarPanelsRef.current.push(panel);
              
            } catch (err) {
              console.error('❌ Failed to place 3D model:', err);
            }
          } else {
            // Normal mode: Set pin position (for Solar API) but don't show marker
            setPinPosition({ lat, lng });
          }
        });

        // Listen for camera changes
        map.addEventListener('camera_changed', (event: any) => {
          const detail = event?.detail;
          if (!detail) return;
          
          const { center: newCenter, heading, tilt, range } = detail;
          setCameraState({
            center: {
              lat: newCenter?.lat || center.lat,
              lng: newCenter?.lng || center.lng,
              altitude: newCenter?.altitude || 60,
            },
            heading: heading || 0,
            tilt: tilt || 0,
            range: range || 200,
          });
        });

        setIsReady(true);
        if (onMapReady) onMapReady(map);
        
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    const timeout = setTimeout(initMap, 1000);
    return () => clearTimeout(timeout);
  }, []);

  // Place marker when pin position changes OR when map becomes ready (ONLY in normal mode)
  useEffect(() => {
    const placeMarker = async () => {

      // Don't place marker if we're in a drawing mode
      if (drawingMode !== 'none') {
        return;
      }

      if (!mapRef.current || !pinPosition || !isReady) {
        return;
      }

      try {
        const { Marker3DElement, AltitudeMode } = await window.google.maps.importLibrary('maps3d') as any;

        // Remove existing marker
        if (markerRef.current) {
          markerRef.current.remove();
        }

        // Create marker at 5m above ground (will adapt to terrain/buildings automatically)
        const marker = new Marker3DElement({
          position: { 
            lat: pinPosition.lat, 
            lng: pinPosition.lng, 
            altitude: 5
          },
          altitudeMode: AltitudeMode.RELATIVE_TO_GROUND,
        });

        mapRef.current.appendChild(marker);
        markerRef.current = marker;

      } catch (error) {
        console.error('❌ Error placing marker:', error);
      }
    };

    if (pinPosition) {
      placeMarker();
    }
  }, [pinPosition, drawingMode, isReady]); // Added isReady as dependency

  // Log building insights when received (don't zoom - keep user's camera position)
  useEffect(() => {
    if (buildingInsights) {
      const sp = buildingInsights.solarPotential;
      if (sp) {
        const roofSegments = sp.roofSegmentStats ?? [];
      }
    }
  }, [buildingInsights]);

  // Clean up drawing elements when mode changes
  useEffect(() => {
    if (drawingMode === 'none') {
      // Clear polygon drawing
      if (drawingPolygonRef.current) {
        drawingPolygonRef.current.remove();
        drawingPolygonRef.current = null;
      }
      drawingPointsRef.current = [];
      drawingMarkersRef.current.forEach(m => m?.remove());
      drawingMarkersRef.current = [];
      
      // Clear measurement line
      if (measureLineRef.current) {
        measureLineRef.current.remove();
        measureLineRef.current = null;
      }
      measurePointsRef.current = [];
      measureMarkersRef.current.forEach(m => m?.remove());
      measureMarkersRef.current = [];
      
      // Clear solar panels
      solarPanelsRef.current.forEach(p => p?.remove());
      solarPanelsRef.current = [];
      
    } else if (drawingMode === 'polygon') {
      // Clear measurement when switching to polygon mode
      if (measureLineRef.current) {
        measureLineRef.current.remove();
        measureLineRef.current = null;
      }
      measurePointsRef.current = [];
      measureMarkersRef.current.forEach(m => m?.remove());
      measureMarkersRef.current = [];
    } else if (drawingMode === 'measure') {
      // Clear polygon when switching to measure mode
      if (drawingPolygonRef.current) {
        drawingPolygonRef.current.remove();
        drawingPolygonRef.current = null;
      }
      drawingPointsRef.current = [];
      drawingMarkersRef.current.forEach(m => m?.remove());
      drawingMarkersRef.current = [];
    } else if (drawingMode === 'panel') {
      // Don't clear panels when switching to panel mode (keep existing panels)
    }
  }, [drawingMode]);

  // Update camera when external changes occur
  useEffect(() => {
    if (!mapRef.current || !cameraState.center) return;

    const map = mapRef.current;
    map.center = cameraState.center;
    if (cameraState.tilt !== undefined) map.tilt = cameraState.tilt;
    if (cameraState.heading !== undefined) map.heading = cameraState.heading;
    if (cameraState.range !== undefined) map.range = cameraState.range;
  }, [cameraState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapRef.current && containerRef.current?.contains(mapRef.current)) {
        containerRef.current.removeChild(mapRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#f0f0f0',
      }}
    >
      {is3DUnavailable ? (
        <Map3DUnavailableOverlay />
      ) : !isReady && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#333',
          fontSize: '1.2em',
          fontFamily: 'sans-serif'
        }}>
          Loading 3D Map...
        </div>
      )}
    </div>
  );
}

